import PromotionModal from '@basicComponents/sequential-modal/promotion-modal';
import React, {useCallback, useEffect} from 'react';
import {sequentialStorage} from '@utils/Storage/SequentialStorage';
import {useModalManager} from '@basicComponents/sequential-modal/ModalManagerProvider';
import HomeYue from './home-yue';
import {getReward} from '@/common-pages/luckyspin/luckyspin.service';
import globalStore from '@services/global.state';
import CheckInModal, {CHECK_IN_NAME} from '@basicComponents/check-in-modal';
import ExchangeRewardSuccess, {
  EXCHANGE_REWARD_SUCCESS,
} from './exchange-reward-success';
import {getExchangeAutoCode} from '../home.service';
import RechargeBonusContent from './recharge-bonus-content';
import RechargeGiftModal from './recharge-gift/RechargeGiftModal';
import {
  getCurrentRechargeGift,
  type RechargeGiftScene,
  type RechargeGiftTier,
} from './recharge-gift/recharge-gift.service';
import {rechargeGiftModalRequest} from './recharge-gift/recharge-gift-trigger';
import {
  isRechargeGiftCooldownActive,
  startRechargeGiftCooldown,
} from './recharge-gift/recharge-gift-cooldown';
import {
  filterRechargeGiftRouteEntries,
  hasAvailableRechargeGiftTier,
} from './recharge-gift/recharge-gift-eligibility';
import {
  canShowRechargeGiftPopup,
  markRechargeGiftPopupShown,
} from './recharge-gift/recharge-gift-popup-count';

const HOME_SEQUENTIAL = 'home-sequential';
const REWARD_TITLE = 'reward';
const RECHARGE_BONUS_POPUP_TITLE = 'RechargeBonusPopup';
const RECHARGE_GIFT_TITLE = 'RECHARGE_GIFT_EVENT';

interface RechargeGiftPopupData extends RechargeGiftTier {
  title: typeof RECHARGE_GIFT_TITLE;
  scene: RechargeGiftScene;
  bannerId?: string | number;
}

const getRechargeGiftPopupData = async (
  triggerItem: any,
): Promise<RechargeGiftPopupData | null> => {
  if (!globalStore.token) {
    return null;
  }
  const gift = await getCurrentRechargeGift('LOGIN_HOME').catch(() => null);
  const hasEnabledTier = hasAvailableRechargeGiftTier(gift);
  if (
    !gift ||
    !hasEnabledTier ||
    isRechargeGiftCooldownActive('HOME_POPUP', gift.interceptCooldownHours) ||
    !canShowRechargeGiftPopup(gift.dailyPopupLimit)
  ) {
    return null;
  }
  return {
    ...gift,
    title: RECHARGE_GIFT_TITLE,
    scene: 'LOGIN_HOME',
    bannerId: triggerItem.bannerId,
  };
};

const getRechargeBonusRewardAmount = (popUrl?: string) => {
  if (!popUrl || typeof popUrl !== 'string') {
    return 0;
  }
  const [, query = ''] = popUrl.split('?');
  if (!query) {
    return 0;
  }
  const amount = Number(new URLSearchParams(query).get('rewardAmount'));
  return Number.isFinite(amount) && amount > 0 ? amount : 0;
};

const shouldShowSequentialItem = (item: any) => {
  if (item.title !== RECHARGE_BONUS_POPUP_TITLE) {
    return true;
  }
  return getRechargeBonusRewardAmount(item.popUrl) > 0;
};

const getExchangeAmount = (data: any) => {
  if (!data || typeof data !== 'object') {
    return 0;
  }
  return Number(data.totalAmount) || 0;
};

const rewardModalData = async () => {
  if (!globalStore.token) {
    return null;
  }
  const res = await getReward();
  if (res) {
    return {
      rewardAmount: res.rewardAmount,
      step: res.stage,
      title: REWARD_TITLE,
    };
  }
  return null;
};
const sequentialData = () => {
  return sequentialStorage.remindData;
};

const exchangeRewardData = async () => {
  if (!globalStore.token) {
    return null;
  }
  const res = await getExchangeAutoCode();
  const rewardAmount = getExchangeAmount(res);
  if (!rewardAmount) {
    return null;
  }
  return {
    title: EXCHANGE_REWARD_SUCCESS,
    totalAmount: rewardAmount,
    ...(res && typeof res === 'object' ? res : {}),
  };
};

const waitForModalManagerIdle = (
  currentModalId: React.MutableRefObject<string | undefined>,
) =>
  new Promise<boolean>(resolve => {
    const startedAt = Date.now();
    const check = () => {
      if (!currentModalId.current) {
        resolve(true);
        return;
      }
      if (Date.now() - startedAt >= 1200) {
        resolve(false);
        return;
      }
      setTimeout(check, 50);
    };
    check();
  });

export default function HomeSequential() {
  const {showModal, hideModal, currentModalId} = useModalManager();
  const sortPop = (data: any[]) => {
    return data.sort((a, b) => {
      const getPriority = (item: any) => {
        if (item.title === EXCHANGE_REWARD_SUCCESS) {
          return 1;
        }
        if (item.title === RECHARGE_BONUS_POPUP_TITLE) {
          return 1;
        }
        if (item.title === REWARD_TITLE) {
          return 2;
        }
        if (item.title === RECHARGE_GIFT_TITLE) {
          return 2;
        }
        if (item.title === CHECK_IN_NAME) {
          return 3;
        }
        return 4;
      };
      return getPriority(a) - getPriority(b);
    });
  };
  const checkPop = (data: any[]) => {
    const isHomePage = location.href.indexOf('/index/home') > -1;
    const modalData = isHomePage
      ? data
      : data.filter(item => item.title === EXCHANGE_REWARD_SUCCESS);

    const modals = sortPop(modalData)
      .map(item => {
        if (item.title === EXCHANGE_REWARD_SUCCESS) {
          return <ExchangeRewardSuccess key={item.title} {...item} />;
        }
        if (item.title === REWARD_TITLE) {
          return (
            <HomeYue
              key={item.title}
              step={item.step}
              reward={item.rewardAmount}
              sequentialConsume={false}
            />
          );
        }
        if (item.title === RECHARGE_BONUS_POPUP_TITLE) {
          return (
            <RechargeBonusContent
              key={item.bannerId}
              imageUrl={item.popImg}
              rewardAmount={getRechargeBonusRewardAmount(item.popUrl)}
              openUrl={item.popUrl}
            />
          );
        }
        if (item.title === RECHARGE_GIFT_TITLE) {
          return (
            <RechargeGiftModal
              key={item.bannerId}
              gift={item}
              scene={item.scene}
              onShown={markRechargeGiftPopupShown}
              onDismiss={() => startRechargeGiftCooldown('HOME_POPUP')}
              sequentialConsume={false}
              sequentialOverlayClassName="bg-black/80"
              sequentialTransitionDuration={0.18}
              sequentialTransitionType="zoom-in-out"
            />
          );
        }
        if (item.title === CHECK_IN_NAME) {
          if (globalStore.token) {
            return <CheckInModal key={item.bannerId} />;
          }
          return null;
        }
        return (
          <PromotionModal
            key={item.bannerId}
            imageUrl={item.popImg}
            openUrl={item.popUrl}
          />
        );
      })
      .filter(Boolean);

    if (modals.length > 0) {
      showModal({
        id: HOME_SEQUENTIAL,
        modals,
      });
    }
  };
  const updatePops = useCallback(() => {
    let timer: NodeJS.Timeout | null = null;
    const fn = () => {
      if (timer) {
        clearTimeout(timer);
      }
      timer = setTimeout(() => {
        Promise.allSettled([
          rewardModalData(),
          sequentialData(),
          exchangeRewardData(),
        ])
          .then(async res => {
            const popList: any[] = [];
            const [rewardSettled, sequentialSettled, exchangeRewardSettled] =
              res;
            if (rewardSettled.status === 'fulfilled' && rewardSettled.value) {
              popList.push(rewardSettled.value);
            }
            if (
              exchangeRewardSettled.status === 'fulfilled' &&
              exchangeRewardSettled.value
            ) {
              popList.push(exchangeRewardSettled.value);
            }
            if (
              sequentialSettled.status === 'fulfilled' &&
              sequentialSettled.value
            ) {
              const visibleSequentialItems =
                await filterRechargeGiftRouteEntries(
                  sequentialSettled.value,
                  (item: any) => item.popUrl,
                  'BANNER',
                );
              const sequentialItems = await Promise.all(
                visibleSequentialItems.map(async (item: any) => {
                  if (item.title === RECHARGE_GIFT_TITLE) {
                    return getRechargeGiftPopupData(item);
                  }
                  return shouldShowSequentialItem(item) ? item : null;
                }),
              );
              popList.push(...sequentialItems.filter(Boolean));
            }
            return popList;
          })
          .then(data => {
            checkPop(data);
          });
      }, 1000);
    };
    // 数据更新
    sequentialStorage.on('updated', fn);
    sequentialStorage.loadData();
    return () => {
      console.log('销毁');
      sequentialStorage.off('updated');
      if (currentModalId.current === HOME_SEQUENTIAL) {
        hideModal(HOME_SEQUENTIAL);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const off = updatePops();
    return off;
  }, [updatePops]);
  useEffect(() => {
    const subscription = rechargeGiftModalRequest.subscribe(request => {
      const handleIneligible = () => {
        request.onIneligible?.();
        request.onUnavailable?.();
      };
      if (!globalStore.token) {
        handleIneligible();
        return;
      }
      getCurrentRechargeGift(request.scene)
        .then(gift => {
          if (
            request.scene === 'RECHARGE_ENTRY' &&
            gift.rechargeInterceptStatus === 0
          ) {
            request.onUnavailable?.();
            return;
          }
          const hasEnabledTier = hasAvailableRechargeGiftTier(gift);
          if (!hasEnabledTier) {
            handleIneligible();
            return;
          }
          if (
            request.scene === 'RECHARGE_ENTRY' &&
            isRechargeGiftCooldownActive(
              'RECHARGE_ENTRY',
              gift.interceptCooldownHours,
            )
          ) {
            request.onUnavailable?.();
            return;
          }
          const handleDismiss = () => {
            if (request.scene === 'RECHARGE_ENTRY') {
              startRechargeGiftCooldown('RECHARGE_ENTRY');
            }
            request.onDismiss?.();
          };
          return waitForModalManagerIdle(currentModalId).then(isIdle => {
            if (!isIdle) {
              request.onUnavailable?.();
              return;
            }
            const shown = showModal({
              id: `recharge-gift-${request.scene}`,
              overlayClassName: 'bg-black/80',
              modals: [
                <RechargeGiftModal
                  gift={gift}
                  scene={request.scene}
                  onDismiss={handleDismiss}
                  sequentialOverlayClassName="bg-black/80"
                  sequentialTransitionDuration={0.18}
                  sequentialTransitionType="zoom-in-out"
                />,
              ],
            });
            if (!shown) {
              request.onUnavailable?.();
            }
          });
        })
        .catch(() => {
          handleIneligible();
        });
    });
    return () => subscription.unsubscribe();
  }, [currentModalId, showModal]);
  return null;
}
